import React from "react";
import { Link } from "react-router-dom";
import { websitePaths } from "../../../routes/websitePaths";
import { useFetchPublicProjectImagesQuery } from "../../../store/api/projectImagesApi";
import "../../../components/website/sections/ProjectCategoryPage.css";

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500'%3E%3Crect width='800' height='500' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-size='18' font-family='sans-serif'%3EDialin24 Commercial%3C/text%3E%3C/svg%3E";

type CommercialProject = {
  title: string;
  category: string;
  location: string;
  description: string;
  features: string[];
  images: string[];
};

const categoryHighlights = [
  {
    title: "Office And Workspace Projects",
    description:
      "Professional office environments planned for productivity, branding, and long-term daily use.",
    icon: "bi bi-building",
  },
  {
    title: "Retail And Showroom Spaces",
    description:
      "Commercial spaces designed to improve customer flow, product visibility, and visual appeal.",
    icon: "bi bi-shop",
  },
  {
    title: "Hospitality And Service Spaces",
    description:
      "Guest-focused commercial environments built for comfort, service efficiency, and smooth operations.",
    icon: "bi bi-building-check",
  },
];

const commercialProjects: CommercialProject[] = [
  {
    title: "Corporate Office Tower",
    category: "Corporate Office",
    location: "Hazratganj, Lucknow",
    description:
      "A modern office project planned to support collaborative work, premium reception areas, and efficient business operations.",
    features: [
      "Executive meeting and reception areas",
      "Efficient floor planning for daily workflows",
      "Professional finishes aligned with business branding",
    ],
    images: [
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1497366412874-3415097a27e7?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    title: "Retail Complex Build",
    category: "Retail Development",
    location: "Vibhuti Khand, Lucknow",
    description:
      "A commercial retail destination designed to support customer movement, storefront visibility, and reliable facility performance.",
    features: [
      "Shopfront-focused planning for visibility",
      "Common circulation and access management",
      "Durable flooring and utility-ready commercial areas",
    ],
    images: [
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1519567770579-c2fc5f5e6f0f?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    title: "Hospitality Facility",
    category: "Hospitality Space",
    location: "Kanpur Road, Lucknow",
    description:
      "A guest-ready hospitality project delivered with attention to comfort, welcoming interiors, and organized service areas.",
    features: [
      "Customer-friendly reception and waiting zones",
      "Functional service back-of-house planning",
      "Consistent finish quality for guest-facing areas",
    ],
    images: [
      "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80",
    ],
  },
];

const completedCommercialProjects = [
  {
    title: "Finished Office Interior",
    location: "Lucknow",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Retail Frontage Completion",
    location: "Vibhuti Khand",
    image:
      "https://images.unsplash.com/photo-1517502884422-41eaead166d4?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Commercial Lobby Delivery",
    location: "Hazratganj",
    image:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Hospitality Lounge Finish",
    location: "Lucknow",
    image:
      "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Modern Workspace Setup",
    location: "Noida",
    image:
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Completed Commercial Fit-Out",
    location: "NCR",
    image:
      "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=1200&q=80",
  },
];

const CommercialProjects: React.FC = () => {
  const { data: apiImages = [] } = useFetchPublicProjectImagesQuery("Commercial");

  const resolveImages = (fallbacks: string[], startIdx: number, count = 3): string[] => {
    const slice = apiImages.slice(startIdx, startIdx + count).map((img) => img.imageUrl);
    while (slice.length < count) slice.push(fallbacks[slice.length] ?? PLACEHOLDER);
    return slice;
  };

  const liveProjects = commercialProjects.map((p, i) => ({
    ...p,
    images: resolveImages(p.images, i * 3),
  }));

  const liveCompleted = completedCommercialProjects.map((item, i) => ({
    ...item,
    image: apiImages[i]?.imageUrl ?? item.image,
  }));

  return (
    <section className='project-category-page website-page'>
      <div className='project-category-hero website-page-hero text-center'>
        <div className='container'>
          <p className='website-page-kicker mb-1'>Commercial Portfolio</p>
          <h2 className='project-page-title website-page-title mb-2'>
            Explore commercial projects built for business visibility,
            performance, and customer experience.
          </h2>
          <p className='project-page-subtitle website-page-subtitle mx-auto mb-0'>
            Browse office, retail, and hospitality projects with detailed
            highlights, multiple images, and completed commercial work that
            helps customers evaluate our execution quality.
          </p>
        </div>
      </div>

      <div className='container website-page-content'>
        <div className='row g-4 mb-5'>
          {categoryHighlights.map((item) => (
            <div className='col-md-6 col-lg-4' key={item.title}>
              <article className='project-category-link-card h-100'>
                <div className='project-category-link-icon'>
                  <i className={item.icon}></i>
                </div>
                <h5>{item.title}</h5>
                <p>{item.description}</p>
              </article>
            </div>
          ))}
        </div>

        <div className='project-residential-section-head mb-4'>
          <span className='website-section-kicker'>Project Types</span>
          <h3 className='website-section-title mb-2'>
            Choose the commercial project type that matches your business need.
          </h3>
          <p className='website-section-subtitle mb-0'>
            Each section highlights a different commercial project style with
            business-focused features, location details, and multiple project
            images.
          </p>
        </div>

        <div className='residential-project-list'>
          {liveProjects.map((project) => (
            <article
              className='residential-project-card'
              key={`${project.title}-${project.location}`}
            >
              <div className='residential-project-top'>
                <div>
                  <span className='residential-project-badge'>
                    {project.category}
                  </span>
                  <h5>{project.title}</h5>
                  <p className='residential-project-location'>
                    <i className='bi bi-geo-alt-fill me-2'></i>
                    {project.location}
                  </p>
                </div>
                <Link
                  to={websitePaths.contact}
                  className='btn brand-btn-primary'
                >
                  Discuss Similar Project
                </Link>
              </div>

              <div className='row g-4 align-items-start'>
                <div className='col-lg-7'>
                  <div className='residential-gallery-grid'>
                    <div className='residential-gallery-main'>
                      <img src={project.images[0]} alt={project.title} />
                    </div>
                    <div className='residential-gallery-stack'>
                      {project.images.slice(1).map((image, index) => (
                        <div
                          className='residential-gallery-small'
                          key={`${project.title}-${index}`}
                        >
                          <img
                            src={image}
                            alt={`${project.title} view ${index + 2}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className='col-lg-5'>
                  <div className='residential-project-content'>
                    <p className='mb-3'>{project.description}</p>
                    <ul className='residential-project-features'>
                      {project.features.map((feature) => (
                        <li key={`${project.title}-${feature}`}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className='project-residential-section-head completed-projects-head'>
          <span className='website-section-kicker'>Completed Projects</span>
          <h3 className='website-section-title mb-2'>
            See completed commercial projects delivered for business spaces.
          </h3>
          <p className='website-section-subtitle mb-0'>
            This gallery showcases completed office, retail, and hospitality
            work so customers can review the quality, finish, and presentation
            of our delivered commercial projects.
          </p>
        </div>

        <div className='row g-4'>
          {liveCompleted.map((item) => (
            <div
              className='col-sm-6 col-lg-4'
              key={`${item.title}-${item.location}`}
            >
              <article className='completed-project-card'>
                <img
                  src={item.image}
                  alt={item.title}
                  className='completed-project-image'
                />
                <div className='completed-project-overlay'>
                  <h4>{item.title}</h4>
                  <p>{item.location}</p>
                </div>
              </article>
            </div>
          ))}
        </div>

        <div className='project-category-cta mt-4'>
          <div>
            <span className='website-section-kicker'>
              Plan Your Commercial Space
            </span>
            <h3 className='mb-2'>
              Tell us about your office, retail, or hospitality project and our
              team will guide you.
            </h3>
            <p className='mb-0'>
              We help customers plan commercial buildings, fit-outs, showroom
              spaces, and hospitality environments with a clear execution path.
            </p>
          </div>
          <Link to={websitePaths.contact} className='btn brand-btn-primary'>
            Start Commercial Consultation
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CommercialProjects;
